import { RouteModule } from '#/horizon/components/RouteModule';
import { setSurrogate } from '#/horizon/middlewares/surrogate.middleware';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type {
  LayoutHandleElements,
  LayoutHandleProps,
} from '#/layouts/Layout/LayoutHandleProps';
import { salaryApiServerFetchJobSynonym } from '#/modules/salary/api/salaryApiFetchJobSynonym';
import { DynamicSearchJobsRefinements } from '#/modules/search/jobs/components/Refinements/DynamicSearchJobsRefinements';
import { searchJobsRoutePathByLocale } from '#/modules/search/jobs/routing/searchJobs.route-paths';
import { searchJobsRouteReference } from '#/modules/search/routing/search.routes';
import { getSearchPageLinkRels } from '#/modules/search/utils/getSearchPageLinkRels';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import type { HorizonAppRouteShouldRevalidateFunctionArgs } from '#/routing/types/HorizonAppRouteShouldRevalidateFunctionArgs';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import { SearchJobsPage } from './SearchJobsPage';

export const handle = {
  searchRefinements: <DynamicSearchJobsRefinements />,
} satisfies LayoutHandleElements;

export async function loader({
  request,
  url,
  context,
}: HorizonAppRouteLoaderArgs<typeof searchJobsRouteReference>) {
  setSurrogate({ context, data: { type: 'hardcoded-route' } });
  const synonymId = url.searchParams.get('synonymId');

  const synonym = synonymId
    ? await salaryApiServerFetchJobSynonym({ request, context })(
        synonymId,
      ).catch(() => null)
    : null;

  const { serverContext } = getServerRouterContext(context);

  return {
    handle: {
      urlByLocale: searchJobsRoutePathByLocale,
      defaultSearchType: 'JOB',
      hideFooterDivider: true,
      searchFromPageType: 'SEARCH',
      showSearchRefinements: true,
      showDesktopSearch: true,
      isSearchPage: true,
      hideMobileHeaderOnScroll: true,
    } satisfies LayoutHandleProps,
    linkRels: getSearchPageLinkRels({
      searchParams: url.searchParams,
      locale: serverContext.locale,
      routePathByLocale: searchJobsRoutePathByLocale,
      referenceRoute: searchJobsRouteReference,
    }),
    synonym,
    now: serverContext.now,
  };
}

export function shouldRevalidate({
  currentUrl,
  defaultShouldRevalidate,
  nextUrl,
}: HorizonAppRouteShouldRevalidateFunctionArgs<
  typeof searchJobsRouteReference
>) {
  if (currentUrl.searchParams.size === nextUrl.searchParams.size) {
    return false;
  }

  return defaultShouldRevalidate;
}

export default function SearchJobsHorizonRoute({
  loaderData: { linkRels, now },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n, locale } = useI18n();

  const ogImage = (() => {
    switch (locale) {
      case I18nLocale.EN:
        return getStaticImageSrc('og-image-en-jobs.jpg');
      case I18nLocale.ES:
        return;
      case I18nLocale.PT:
        return getStaticImageSrc('og-image-pt-jobs.jpg');
    }
  })();

  return (
    <RouteModule
      title={
        /* TRANSLATORS: job search page title */
        i18n('Best Jobs Near You, Remote Jobs, Nonprofit Jobs')
      }
      description={
        /* TRANSLATORS: job search page description */
        i18n(
          'Join Idealist to find a social-impact job and take your career to the next level',
        )
      }
      linkRels={linkRels}
      ogImage={ogImage}
    >
      <SearchJobsPage now={now} />
    </RouteModule>
  );
}
